import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

/** A consequence preview: what choosing an action will spawn next. */
export interface ActionConsequence {
  consequence_key: string;
  label: string;
  opens_title: string;
  opens_owner: string;
  deadline_param_key: string | null;
  requires_event_date: boolean;
  deadline_basis: string;
  rule_cite: string | null;
}

/** One available move at an open segment. */
export interface SegmentAction {
  action_key: string;
  label: string;
  short_desc: string | null;
  actor: string;
  rule_cite: string | null;
  brief_required: boolean;
  is_inaction: boolean;
  is_terminal: boolean;
  closes_segment: boolean;
  consequences: ActionConsequence[];
}

/** A segment = a window of time opened by an event, owned by a party. */
export interface CaseSegment {
  id: string;
  parent_segment_id: string | null;
  stage_key: string;
  party_id: string | null;
  metadata: Record<string, unknown> | null;
  title: string;
  owner: string;
  status: "open" | "acted" | "resolved" | "expired" | "waived";
  trigger_label: string | null;
  trigger_date: string | null;
  deadline_date: string | null;
  deadline_basis: string;
  days_remaining: number | null;
  overdue: boolean;
  rule_cite: string | null;
  param_label: string | null;
  chosen_action_key: string | null;
  opened_by_action_key: string | null;
  created_at: string;
  taken_actions: string[];
  actions: SegmentAction[];
}

export interface BoardFiling {
  id: string;
  filing_kind: string;
  title: string;
  filed_at: string | null;
  filed_by: string | null;
  is_operative: boolean;
}

export interface PartyRow {
  party_id: string;
  display_name: string;
  party_type: string;
  anchored: boolean;
  deadline_date: string | null;
  days_remaining: number | null;
  overdue: boolean;
}

export interface ActionBoard {
  needs_anchor: boolean;
  parties: PartyRow[];
  anchor_filing: { id: string; title: string; filing_kind: string; filed_at: string | null } | null;
  current: {
    segment_id: string;
    party_id: string | null;
    title: string;
    owner: string;
    deadline_date: string | null;
    days_remaining: number | null;
    overdue: boolean;
    rule_cite: string | null;
    param_label: string | null;
    trigger_label: string | null;
    trigger_date: string | null;
  } | null;
  segments: CaseSegment[];
  filings: BoardFiling[];
  ruleset: {
    code: string;
    name: string;
    version: string;
    rules_note?: string;
    holiday_years?: number[];
    holiday_source?: string;
  } | null;
}

const EMPTY: ActionBoard = {
  needs_anchor: false,
  parties: [],
  anchor_filing: null,
  current: null,
  segments: [],
  filings: [],
  ruleset: null,
};

/** The whole case action board: deadline, history, and the live action menu. */
export function useCaseActionBoard(caseId: string | undefined) {
  return useQuery<ActionBoard, Error>({
    queryKey: ["action-board", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await berserkr().rpc("compute_case_action_board", {
        p_case_id: caseId,
      });
      if (error) throw new Error(error.message);
      return (data as ActionBoard) ?? EMPTY;
    },
  });
}

/** Anchor (or re-anchor) the response clock from a date of service. */
export function useSetServiceAnchor(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { filingId: string; partyId: string; servedAt: string }
  >({
    mutationFn: async ({ filingId, partyId, servedAt }) => {
      const { data, error } = await berserkr().rpc("set_case_service_anchor", {
        p_case_id: caseId,
        p_filing_id: filingId,
        p_party_id: partyId,
        p_served_at: servedAt,
      });
      if (error) throw new Error(error.message);
      return data as string;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["action-board", caseId] }),
  });
}

/** Anchor several defendants to the same service date (served together). */
export function useSetServiceAnchorBulk(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { filingId: string; partyIds: string[]; servedAt: string }
  >({
    mutationFn: async ({ filingId, partyIds, servedAt }) => {
      const { data, error } = await berserkr().rpc("set_case_service_anchor_bulk", {
        p_case_id: caseId,
        p_filing_id: filingId,
        p_party_ids: partyIds,
        p_served_at: servedAt,
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["action-board", caseId] }),
  });
}

/** Group action: fan one move out to each individual's segment. */
export function useAdvanceSegmentsBulk(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { segmentIds: string[]; actionKey: string; eventDate?: string | null; note?: string | null }
  >({
    mutationFn: async ({ segmentIds, actionKey, eventDate, note }) => {
      const { data, error } = await berserkr().rpc("advance_case_segments_bulk", {
        p_segment_ids: segmentIds,
        p_action_key: actionKey,
        p_event_date: eventDate ?? null,
        p_note: note ?? null,
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["action-board", caseId] }),
  });
}

/** Take an action on a segment; the server resolves it and spawns the next. */
export function useAdvanceSegment(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { segmentId: string; actionKey: string; eventDate?: string | null; note?: string | null; meta?: Record<string, unknown> }
  >({
    mutationFn: async ({ segmentId, actionKey, eventDate, note, meta }) => {
      const { data, error } = await berserkr().rpc("advance_case_segment", {
        p_segment_id: segmentId,
        p_action_key: actionKey,
        p_event_date: eventDate ?? null,
        p_note: note ?? null,
        p_meta: meta ?? {},
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      // an Answer may raise counterclaims (new claim rows) — refresh the volley too
      qc.invalidateQueries({ queryKey: ["action-board", caseId] });
      qc.invalidateQueries({ queryKey: ["claims-summary", caseId] });
    },
  });
}

/** Record a Motion-to-Dismiss ruling: per-claim outcomes set each claim's fate,
 *  the overall ruling is derived, and the next segment spawns. */
export function useRecordMtdRuling(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { segmentId: string; rulingDate: string; outcomes: { claim_id: string; outcome: string }[] }
  >({
    mutationFn: async ({ segmentId, rulingDate, outcomes }) => {
      const { data, error } = await berserkr().rpc("record_mtd_ruling", {
        p_segment_id: segmentId,
        p_ruling_date: rulingDate,
        p_outcomes: outcomes,
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["action-board", caseId] });
      qc.invalidateQueries({ queryKey: ["claims-summary", caseId] });
      qc.invalidateQueries({ queryKey: ["case-claims", caseId] });
    },
  });
}

/** Undo the most recent action — removes the segment(s) it spawned and
 *  reopens its parent so it can be redone (e.g. after a wrong date). */
export function useUndoLastAction(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, void>({
    mutationFn: async () => {
      const { data, error } = await berserkr().rpc("undo_last_case_action", {
        p_case_id: caseId,
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      // undoing an Answer pulls back the counterclaims it raised
      qc.invalidateQueries({ queryKey: ["action-board", caseId] });
      qc.invalidateQueries({ queryKey: ["claims-summary", caseId] });
    },
  });
}

/** Wipe the entire docket history for a case (back to the service-date prompt). */
export function useClearActionHistory(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, void>({
    mutationFn: async () => {
      const { error } = await berserkr().rpc("clear_case_action_history", {
        p_case_id: caseId,
      });
      if (error) throw new Error(error.message);
      return null;
    },
    onSuccess: () => {
      // clearing the docket also clears docket-raised counterclaims
      qc.invalidateQueries({ queryKey: ["action-board", caseId] });
      qc.invalidateQueries({ queryKey: ["claims-summary", caseId] });
    },
  });
}

/** A verified case example attached to an action (only verified ones load). */
export interface PlaybookCase {
  case_name: string;
  citation: string | null;
  court: string | null;
  decided_year: number | null;
  holding: string | null;
  outcome: string | null;
  similarity_note: string | null;
  source_url: string | null;
  verified_on: string | null;
}

/** Authored, rule-verified reference for one action (stored, not generated). */
export interface PlaybookEntry {
  action_key: string;
  stage_key: string;
  summary: string;
  when_used: string | null;
  consequences: string | null;
  skeleton: string | null;
  rule_cites: string[] | null;
  edition: string;
  verified_on: string;
  cases: PlaybookCase[];
}

/** The action playbook for a ruleset (reference data; cached aggressively). */
export function useActionPlaybook(ruleset = "nc-business-court") {
  return useQuery<PlaybookEntry[], Error>({
    queryKey: ["action-playbook", ruleset],
    enabled: isSupabaseConfigured(),
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await berserkr().rpc("list_action_playbook", {
        p_ruleset: ruleset,
      });
      if (error) throw new Error(error.message);
      return (data as PlaybookEntry[]) ?? [];
    },
  });
}
